import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sparkles,
  Target,
  Rocket,
  Heart,
  ShieldCheck,
  Globe,
  Zap,
  Users,
} from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { useAppSettings } from "@/hooks/useAppSettings";
import SeoHead from "@/components/SeoHead";

export default function About() {
  const { brand } = useAppSettings();
  const brandName = brand.name || "StoreSEO AI";

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SeoHead title="About" />
      <header className="container mx-auto px-4 py-4 sm:py-6 flex items-center justify-between gap-2">
        <Link to="/"><BrandMark size="sm" /></Link>
        <div className="flex gap-1 sm:gap-2 items-center">
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><Link to="/">Home</Link></Button>
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><Link to="/#pricing">Pricing</Link></Button>
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><Link to="/auth">Sign In</Link></Button>
          <Button asChild size="sm" className="bg-gradient-primary shadow-glow"><Link to="/auth">Get Started</Link></Button>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-12 pb-24">
        {/* Hero */}
        <section className="relative text-center max-w-3xl mx-auto">
          <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden>
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-blob" />
            <div className="absolute top-10 right-1/4 w-72 h-72 bg-accent/20 rounded-full blur-3xl animate-blob animation-delay-2000" />
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-background/60 backdrop-blur px-4 py-1.5 text-xs font-medium mb-6">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-gradient font-semibold">About {brandName}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
            We help stores <span className="text-gradient-animated">get found</span> and{" "}
            <span className="text-gradient-animated">get sold</span>.
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {brandName} is an AI-powered SEO copilot for e-commerce. We turn
            messy product details into high-converting listings - titles, descriptions,
            keywords, and tags - in seconds, so you can spend less time writing and
            more time selling.
          </p>
        </section>

        {/* Mission / Vision */}
        <section className="grid md:grid-cols-2 gap-6 mt-20 max-w-5xl mx-auto">
          <Card className="p-7 bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow">
            <div className="h-11 w-11 rounded-lg bg-gradient-primary/10 flex items-center justify-center mb-4">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Our mission</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Make professional, search-ready product copy accessible to every
              merchant - not just the ones with a marketing team. One click, one
              listing, ready to publish.
            </p>
          </Card>
          <Card className="p-7 bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow">
            <div className="h-11 w-11 rounded-lg bg-gradient-primary/10 flex items-center justify-center mb-4">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Our vision</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A world where every small store can compete on the search results
              page with the biggest brands - because great copy is no longer a
              luxury, it's a button.
            </p>
          </Card>
        </section>

        {/* What we do */}
        <section className="mt-24 max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              What we do
            </h2>
            <p className="text-muted-foreground">
              A focused toolkit built around one goal: listings that rank and convert.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: "Instant generation",
                desc: "SEO titles, long-form descriptions, 10 keywords, and 10 tags - generated in under 3 seconds.",
              },
              {
                icon: ShieldCheck,
                title: "Quality scoring",
                desc: "Every listing is scored 0–100 so you know it's ready for Shopify, Amazon, eBay, AliExpress, Etsy or any storefront.",
              },
              {
                icon: Globe,
                title: "Built for any store",
                desc: "Output is platform-agnostic. Copy, paste, export - works wherever you sell.",
              },
            ].map((f) => (
              <Card
                key={f.title}
                className="p-6 bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow"
              >
                <div className="h-10 w-10 rounded-lg bg-gradient-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1.5">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="mt-24 max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              What we believe in
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: "Merchants first",
                desc: "Every feature is shaped by real store owners. If it doesn't help you sell, it doesn't ship.",
              },
              {
                icon: ShieldCheck,
                title: "Privacy by default",
                desc: "Your products, your data. AI calls run through hardened serverless functions - never shared, never resold.",
              },
              {
                icon: Users,
                title: "Honest pricing",
                desc: "Free forever to try. Paid plans only when you're ready. No surprise fees, cancel anytime.",
              },
            ].map((v) => (
              <Card
                key={v.title}
                className="p-6 bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow"
              >
                <div className="h-10 w-10 rounded-lg bg-gradient-primary/10 flex items-center justify-center mb-4">
                  <v.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1.5">{v.title}</h3>
                <p className="text-sm text-muted-foreground">{v.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-24 max-w-3xl mx-auto">
          <Card className="relative overflow-hidden p-10 text-center bg-gradient-card border-primary/30 shadow-elegant">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/20 rounded-full blur-3xl" aria-hidden />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-accent/20 rounded-full blur-3xl" aria-hidden />
            <h2 className="relative text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Ready to write listings that <span className="text-gradient">sell</span>?
            </h2>
            <p className="relative text-muted-foreground mb-6">
              Start free. No credit card. 5 generations a day, on us.
            </p>
            <div className="relative flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-gradient-primary shadow-glow">
                <Link to="/auth">Get started free</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/#pricing">See pricing</Link>
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <footer className="border-t bg-background/50">
        <div className="container mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} {brandName}. All rights reserved.</div>
          <nav className="flex gap-6">
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/terms" className="hover:text-foreground">Terms & Conditions</Link>
            <Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy Notice</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
